import React, { Fragment, useEffect, useRef, useState } from 'react';
import {
  Link,
  NavLink,
  useLocation,
  useNavigate,
  matchPath,
} from 'react-router-dom';
import styles from './Sidebar.module.scss';
import { BOT_SETTINGS, ICON_MAP } from './factory/SideBarData';
import { useFetch } from 'hooks/useFetch';
import { notificationsEndpoints, scriptEndpoints } from 'hooks/endpoints';
import SidebarFooter from './components/footer/SidebarFooter';
import replaceIdInPath from './factory/ReplaceIdInPath';
import { useUserContext } from 'app-context/user/UserContext';
import Loader from 'component/atom/loader/Loader';
import SvgWithBranding from 'component/hoc/branding/withBrandingColor.hoc';
import NotificationsPanel from './components/notifications-panel/NotificationsPanel';
import { useNotifications } from 'notifications/useNotifications';
import { cleanToken } from 'utils';
import bellIcon from '../../../assets/icons/sidebar-icons/bell-icon.svg';

const eduliticsLogo = '/src/svg/sidebar/educator-logo.svg';
const downArrow = '/src/svg/arrows/chevron-down.svg';

const getIconSrc = (icon) => {
  if (!icon) return '';
  return icon.startsWith('icon-') ? ICON_MAP[icon] : icon;
};
const PATH_REDIRECTS = [
  { match: /^\/workflow\/[^/]+$/, redirect: (id) => `/bot/Workflow/${id}` },
  {
    match: /^\/bot\/agent\/[^/]+$/,
    redirect: (id) => `/bot/link-to-agent/${id}`,
  },
  {
    match: /^\/bot\/data-preview\/[^/]+$/,
    redirect: (id) => `/bot/link-to-agent/${id}`,
  },
  {
    match: /^\/bot\/form\/[^/]+(\/[^/]+)?$/,
    redirect: (id) => `/bot/form-responses/${id}`,
  },
  {
    match: /^\/bot\/edit-form\/[^/]+(\/[^/]+)?$/,
    redirect: (id) => `/bot/form-responses/${id}`,
  },
  {
    match: /^\/bot\/form-leads\/.+$/,
    redirect: (id) => `/bot/form-responses/${id}`,
  },
  {
    match: /^\/bot\/contact-profile\/[^/]+(\/[^/]+)?$/,
    redirect: (id) => `/bot/form-responses/${id}`,
  },
  { match: /^\/knowledge-base\/[^/]+$/, redirect: () => `/knowledge-base` },
];

const SidebarNEW = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedBot, setSelectedBot } = useUserContext();
  // const { sidebarData, refetchSidebarData, isFetching } = useSidebarData();
  const [isBotOpen, setIsBotOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState({});
  const [showNotifications, setShowNotifications] = useState(false);
  const botDropdownRef = useRef(null);
  const token = cleanToken(localStorage.getItem('token'));
  const {
    data: BotsData,
    isLoading,
    isFetching,
    refetch,
  } = useFetch(
    ['botsData'],
    scriptEndpoints.getBots(),
    {
      enable: true,
    },
    {
      pageNumber: 1,
      pageSize: 2000,
    },
  );

  const {
    data: notifications,
    isLoading: isLoadingNotifications,
    isFetching: isFetchingNotifications,
    refetch: refetchNotifications,
  } = useFetch(
    ['notifications'],
    notificationsEndpoints.getAll,
    {
      enable: true,
    },
    {
      pageNumber: 1,
      pageSize: 5000,
    },
  );

  useNotifications({
    token,
    onNotification: (dto) => {
      refetchNotifications();
    },
  });

  const handleBotPick = (bot) => {
    const prevId = selectedBot?.botId;
    localStorage.setItem('selectedBot', JSON.stringify(bot));
    setSelectedBot(bot);
    setIsBotOpen(false);

    if (prevId) {
      const nextPathname = replaceIdInPath(
        location.pathname,
        prevId,
        bot.botId,
      );

      if (nextPathname && nextPathname !== location.pathname) {
        navigate(`${nextPathname}${location.search}${location.hash}`, {
          replace: true,
        });
      }
    }

    let redirected = false;

    for (const { match, redirect } of PATH_REDIRECTS) {
      if (match.test(location.pathname)) {
        const nextPath = redirect(bot.botId);
        navigate(`${nextPath}${location.search}${location.hash}`, {
          replace: true,
        });
        redirected = true;
        break;
      }
    }
    // console.log('Vdfvsjv', redirected);
    // if (!redirected) {
    //   console.log('Stayed on current page:', location.pathname);
    // }
  };

  const toggleMenu = (label) => {
    setOpenMenus((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  useEffect(() => {
    const items = BotsData?.data;
    if (!items) return;
    if (selectedBot && !items.some((b) => b.botId === selectedBot.botId)) {
      setSelectedBot(null);
      localStorage.removeItem('selectedBot');
    }
  }, [BotsData?.data, selectedBot, setSelectedBot]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (!botDropdownRef.current) return;
      if (!botDropdownRef.current.contains(e.target)) setIsBotOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    const storedBot = localStorage.getItem('selectedBot');
    if (storedBot) {
      try {
        setSelectedBot(JSON.parse(storedBot));
      } catch {
        localStorage.removeItem('selectedBot');
      }
    }
  }, []);

  useEffect(() => {
    if (!selectedBot?.botId) return;
    const updated = BotsData?.data?.find((b) => b.botId === selectedBot.botId);
    if (updated) {
      const merged = { ...selectedBot, ...updated };
      setSelectedBot(merged);
      localStorage.setItem('selectedBot', JSON.stringify(merged));
    }
  }, [BotsData?.data, selectedBot?.botId, setSelectedBot]);

  const ACTIVE_ALIASES = {
    '/bot/form-responses/:id': ['/bot/form/:botId/:formId'],
  };

  return (
    <nav className={styles['sidebar']}>
      <div className={styles['content']}>
        <div>
          <div className={styles['logo-container']}>
            <img src={eduliticsLogo} alt='Logo' />
            <div className={styles['notifications-icon-container']}>
              <img
                src={bellIcon}
                alt='Notifications'
                className={styles['bell-icon']}
                onClick={() => {
                  setShowNotifications((prev) => !prev);
                }}
              />
              {notifications?.data?.totalUnreads &&
              notifications?.data?.totalUnreads !== 0 ? (
                <div className={styles['unread-notifications']}>
                  {notifications?.data?.totalUnreads}
                </div>
              ) : (
                <></>
              )}
            </div>
          </div>
        </div>

        <div className={styles['main-navigation']}>
          {BOT_SETTINGS.filter((item) => item.isMainLink).map(
            ({ routeTo: to, routeTitle: label, routeIcon: icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `${styles['nav-link']} ${isActive ? styles['active'] : ''}`
                }
              >
                <img src={getIconSrc(icon)} alt='' />
                <span>{label}</span>
              </NavLink>
            ),
          )}
        </div>
        <div className={styles['bot-selection']} ref={botDropdownRef}>
          <p>Select Bot</p>
          <div
            className={`${styles['dropdown-container']} ${
              !selectedBot ? styles['empty-bot'] : ''
            }`}
            onClick={() => setIsBotOpen(!isBotOpen)}
          >
            {selectedBot ? (
              <div className={styles['bot']}>
                {selectedBot?.src ? (
                  <img
                    className={styles['bot-image']}
                    src={selectedBot?.src}
                    alt=''
                  />
                ) : (
                  <div className={styles['bot-default']}>
                    <p>
                      {selectedBot?.botName
                        ?.split(' ')
                        .filter(Boolean)
                        .map((word) => word.charAt(0).toUpperCase())
                        .slice(
                          0,
                          selectedBot?.botName?.trim().split(/\s+/).length > 2
                            ? 3
                            : undefined,
                        )
                        .join('')}
                    </p>
                  </div>
                )}

                <p className={styles['bot-name']}>{selectedBot.botName}</p>
              </div>
            ) : (
              <div className={styles['bot']}>
                <span>Choose a bot</span>
              </div>
            )}
            <img src={downArrow} alt='' className={styles['arrow']} />
            {isBotOpen && (
              <div className={styles['bots-list-container']} role='listbox'>
                <div className={styles['bots-list']} role='listbox'>
                  {isLoading || isFetching ? (
                    <Loader />
                  ) : (
                    BotsData?.data?.map((bot) => (
                      <div
                        key={bot.botId}
                        type='button'
                        role='option'
                        aria-selected={selectedBot?.botId === bot.botId}
                        className={`${styles['bot-item']} ${
                          selectedBot?.botId === bot.botId
                            ? styles['active']
                            : ''
                        }`}
                        onClick={() => handleBotPick(bot)}
                      >
                        <div className={styles['bot']}>
                          {selectedBot?.src ? (
                            <img
                              className={styles['bot-image']}
                              src={selectedBot?.src}
                              alt=''
                            />
                          ) : (
                            <div className={styles['bot-default']}>
                              <p>
                                {bot?.botName
                                  ?.split(' ')
                                  .map((word) => word.charAt(0))
                                  .join('')
                                  .slice(0, 3)}
                              </p>
                            </div>
                          )}
                        </div>
                        <span className={styles['title']}>{bot.botName}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        {selectedBot ? (
          <div className={styles['bot-settings']}>
            <p className={styles['title']}>Bot Settings</p>
            <div className={styles['menu-container']}>
              <div className={styles['menu']}>
                {BOT_SETTINGS.filter((item) => !item.isMainLink)
                  .filter(
                    (item) =>
                      !(
                        selectedBot?.botType === 0 &&
                        item.routeTitle === 'Deploy'
                      ),
                  )
                  .map(
                    ({
                      routeTitle: label,
                      routeIcon: icon,
                      routeTo: to,
                      children,
                      collapsible,
                    }) => {
                      const isOpen = openMenus[label];
                      const isActive = !collapsible && location.pathname === to;
                      return (
                        <Fragment key={label}>
                          <div
                            className={`${styles['menu-item']} ${
                              !collapsible ? styles['no-arrow'] : ''
                            } ${isActive ? styles['active'] : ''}`}
                            onClick={() => {
                              if (collapsible) {
                                toggleMenu(label);
                                return;
                              }

                              navigate(to);
                            }}
                          >
                            <div
                              className={`${styles['item']} ${
                                isActive ? styles['active'] : ''
                              }`}
                            >
                              <img src={getIconSrc(icon)} alt='' />
                              <p>{label}</p>
                            </div>
                            {collapsible && (
                              <img
                                src={downArrow}
                                alt='Expand'
                                className={isOpen ? styles['up'] : ''}
                              />
                            )}
                          </div>
                          {collapsible && (
                            <div
                              className={`${styles['list-container']} ${
                                isOpen ? styles['open-list'] : ''
                              }`}
                            >
                              <div className={styles['list-inner']}>
                                {children?.map(
                                  ({
                                    routeTitle: childLabel,
                                    routeTo: childTo,
                                    routeIcon: childIcon,
                                  }) => {
                                    const patterns = [
                                      childTo,
                                      ...(ACTIVE_ALIASES[childTo] || []),
                                    ];

                                    const finalPath = childTo.replace(
                                      ':id',
                                      selectedBot?.botId,
                                    );
                                    const isActive = patterns.some((p) =>
                                      matchPath(
                                        { path: p, end: false },
                                        location.pathname,
                                      ),
                                    );

                                    return (
                                      <Link
                                        key={childTo}
                                        to={finalPath}
                                        className={`${styles['nav-link']} ${
                                          isActive ? styles['active'] : ''
                                        }`}
                                      >
                                        <img
                                          src={getIconSrc(childIcon)}
                                          alt=''
                                        />
                                        <span>{childLabel}</span>
                                      </Link>
                                    );
                                  },
                                )}
                              </div>
                            </div>
                          )}
                        </Fragment>
                      );
                    },
                  )}
              </div>
            </div>
          </div>
        ) : null}
      </div>
      <SidebarFooter data={BotsData} />
      <NotificationsPanel
        open={showNotifications}
        onClose={() => setShowNotifications(false)}
        notifications={notifications}
        isLoading={isLoadingNotifications}
        isFetching={isFetchingNotifications}
        refetchNotifications={refetchNotifications}
      />
    </nav>
  );
};

export default SidebarNEW;
